import type {
  ActionMotionIntent,
  ActionPhase,
  ActionTransitionSeed,
  ActiveAction,
  ActiveImpact,
  PresentationEvent,
  PresentationFrame,
  SampledEntity,
  Vec3,
} from "../core";
import { ACTION_MOTION_PRESETS, createActionTransitionSeed } from "../motion";
import { COURT_WARRIOR_KIT_ID } from "../renderer/class-kits";
import type { PresentationSource } from "./types";

const FIXED_STEP = 1 / 120;
const PLAYER_ID = "yard-player";
const BOUNDS = Object.freeze({ x: 6.25, z: 3.9 });
const MAX_SPEED = 3.25;
const ACCELERATION = 11.5;
const DECELERATION = 15;
const HISTORY_LIMIT = 128;

export type YardAbilitySlot = 1 | 2 | 3 | 4;

interface YardActor {
  id: string;
  label: string;
  position: Vec3;
  facingYaw: number;
  health: number;
  maxHealth: number;
  reviveAt: number | null;
}

interface YardAbilityDefinition {
  slot: YardAbilitySlot;
  name: string;
  summary: string;
  cooldown: number;
  phases: Record<ActionPhase, number>;
  motion: ActionMotionIntent;
  range: number;
  arcDot: number;
  damage: number;
  dashDistance?: number;
}

interface ActiveYardAbility {
  definition: YardAbilityDefinition;
  eventId: string;
  startedAt: number;
  targetId?: string;
  impactApplied: boolean;
  transitionFrom?: ActionTransitionSeed;
}

type YardCommand =
  | { at: number; type: "move"; forward: number; right: number }
  | { at: number; type: "ability"; slot: YardAbilitySlot }
  | { at: number; type: "camera"; yaw: number };

export interface YardAbilityState {
  slot: YardAbilitySlot;
  name: string;
  summary: string;
  remaining: number;
}

export const YARD_ABILITIES: readonly YardAbilityDefinition[] = [
  {
    slot: 1,
    name: "Arc Sweep",
    summary: "wide control cut",
    cooldown: 0.28,
    phases: { telegraph: 0.08, commit: 0.15, impact: 0.08, recover: 0.3 },
    motion: ACTION_MOTION_PRESETS.sweep,
    range: 2.35,
    arcDot: -0.15,
    damage: 1,
  },
  {
    slot: 2,
    name: "Needle Rush",
    summary: "narrow advancing thrust",
    cooldown: 0.75,
    phases: { telegraph: 0.06, commit: 0.14, impact: 0.07, recover: 0.34 },
    motion: { ...ACTION_MOTION_PRESETS.thrust, advance: 0.86, reach: 0.92 },
    range: 3.2,
    arcDot: 0.64,
    damage: 2,
  },
  {
    slot: 3,
    name: "Windstep",
    summary: "dash through the line",
    cooldown: 1.2,
    phases: { telegraph: 0.04, commit: 0.13, impact: 0.06, recover: 0.28 },
    motion: { ...ACTION_MOTION_PRESETS.risingSlash, advance: 1, energy: 0.9 },
    range: 2.15,
    arcDot: 0.2,
    damage: 1,
    dashDistance: 1.75,
  },
  {
    slot: 4,
    name: "Sunbreak",
    summary: "close radial impact",
    cooldown: 2.2,
    phases: { telegraph: 0.28, commit: 0.18, impact: 0.1, recover: 0.48 },
    motion: { ...ACTION_MOTION_PRESETS.cleave, energy: 1, reach: 0.7, advance: 0.2 },
    range: 3.05,
    arcDot: -1,
    damage: 2,
  },
] as const;

/**
 * A deliberately tiny authoritative game used as a motion microscope.
 * It owns controls, positions, cooldowns, health, and hit decisions, then
 * projects those facts through the same Presentation IR used by Court.
 */
export class MotionYardSource implements PresentationSource {
  readonly id = "game:motion-yard";
  readonly title = "Motion Yard · Interactive";
  readonly mode = "game" as const;
  readonly canSeek = false;
  readonly duration = 120;

  private currentTime = 0;
  private accumulator = 0;
  private eventSequence = 0;
  private player!: YardActor;
  private dummies: YardActor[] = [];
  private velocity = { x: 0, z: 0 };
  private moveInput = { forward: 0, right: 0 };
  private gaitProgress = 0;
  private activeAbility: ActiveYardAbility | null = null;
  private queuedAbility: YardAbilityDefinition | null = null;
  private readonly cooldownReadyAt = new Map<YardAbilitySlot, number>();
  private readonly history: PresentationEvent[] = [];
  private commands: YardCommand[] = [];
  private replayCommands: YardCommand[] | null = null;
  private replayIndex = 0;
  private replayUntil = 0;
  private cameraYawDegrees = 42;

  constructor() {
    this.resetState();
  }

  get time(): number { return this.currentTime; }
  get events(): readonly PresentationEvent[] { return this.history; }
  get sourceLabel(): string {
    return this.replayCommands ? "Motion Yard · replaying captured input" : "Motion Yard · live input recording";
  }
  get cameraTarget(): Vec3 {
    return { x: this.player.position.x, y: 0.82, z: this.player.position.z };
  }
  get cameraYaw(): number { return this.cameraYawDegrees; }
  get isReplaying(): boolean { return this.replayCommands !== null; }
  get canReplay(): boolean { return this.commands.length > 0 && !this.replayCommands; }

  get abilityStates(): YardAbilityState[] {
    return YARD_ABILITIES.map((ability) => ({
      slot: ability.slot,
      name: ability.name,
      summary: ability.summary,
      remaining: Math.max(0, (this.cooldownReadyAt.get(ability.slot) ?? 0) - this.currentTime),
    }));
  }

  reset(): void {
    this.commands = [];
    this.replayCommands = null;
    this.resetState();
  }

  step(deltaSeconds: number): void {
    this.accumulator += Math.min(0.1, Math.max(0, deltaSeconds));
    while (this.accumulator >= FIXED_STEP) {
      this.simulate(FIXED_STEP);
      this.accumulator -= FIXED_STEP;
    }
  }

  seek(_time: number): void {
    // Live input is replayed from its captured command stream, never scrubbed.
  }

  setMovementInput(forward: number, right: number): void {
    if (this.replayCommands) return;
    const next = { forward: clamp(forward, -1, 1), right: clamp(right, -1, 1) };
    if (next.forward === this.moveInput.forward && next.right === this.moveInput.right) return;
    this.moveInput = next;
    this.commands.push({ at: this.currentTime, type: "move", ...next });
  }

  setCameraYaw(degrees: number): void {
    if (this.replayCommands) return;
    const yaw = ((degrees % 360) + 360) % 360;
    if (Math.abs(yaw - this.cameraYawDegrees) < 0.25) return;
    this.cameraYawDegrees = yaw;
    this.commands.push({ at: this.currentTime, type: "camera", yaw });
  }

  triggerAbility(slot: YardAbilitySlot): boolean {
    if (this.replayCommands) return false;
    const accepted = this.startAbility(slot);
    if (accepted) this.commands.push({ at: this.currentTime, type: "ability", slot });
    return accepted;
  }

  replayRun(): boolean {
    if (!this.canReplay) return false;
    const captured = this.commands.map((command) => ({ ...command }));
    const replayUntil = this.currentTime;
    this.resetState();
    this.commands = captured;
    this.replayCommands = captured;
    this.replayUntil = replayUntil;
    this.replayIndex = 0;
    return true;
  }

  sample(): PresentationFrame {
    return {
      time: this.currentTime,
      duration: this.duration,
      entities: [this.samplePlayer(), ...this.dummies.map((dummy) => this.sampleDummy(dummy)), ...yardMarkers()],
      impacts: this.sampleImpacts(),
      recentEvents: this.history.filter((event) => this.currentTime - event.at <= 1.25),
    };
  }

  private resetState(): void {
    this.currentTime = 0;
    this.accumulator = 0;
    this.eventSequence = 0;
    this.velocity = { x: 0, z: 0 };
    this.moveInput = { forward: 0, right: 0 };
    this.gaitProgress = 0;
    this.activeAbility = null;
    this.queuedAbility = null;
    this.cooldownReadyAt.clear();
    this.history.length = 0;
    this.replayIndex = 0;
    this.cameraYawDegrees = 42;
    this.player = actor(PLAYER_ID, "Wayfarer", 0, -1.65, 8);
    this.player.facingYaw = 0;
    this.dummies = [
      actor("yard-dummy-center", "Center Dummy", 0, 0.35, 4),
      actor("yard-dummy-left", "Left Dummy", -2.35, 1.65, 4),
      actor("yard-dummy-right", "Right Dummy", 2.35, 1.65, 4),
      actor("yard-dummy-rear", "Rear Dummy", 0, 3.15, 4),
    ];
    for (const entry of [this.player, ...this.dummies]) {
      this.addEvent({ id: this.nextId("spawn"), type: "spawn", entityId: entry.id, at: 0, position: { ...entry.position } });
    }
  }

  private simulate(dt: number): void {
    this.applyReplayCommands();
    this.currentTime += dt;
    this.updateMovement(dt);
    this.updateAbility(dt);
    this.updateDummies();
    if (this.replayCommands && this.currentTime >= this.replayUntil + 0.65) {
      this.replayCommands = null;
      this.moveInput = { forward: 0, right: 0 };
    }
  }

  private applyReplayCommands(): void {
    if (!this.replayCommands) return;
    while (this.replayIndex < this.replayCommands.length) {
      const command = this.replayCommands[this.replayIndex]!;
      if (command.at > this.currentTime + 1e-6) break;
      this.replayIndex += 1;
      if (command.type === "move") this.moveInput = { forward: command.forward, right: command.right };
      else if (command.type === "camera") this.cameraYawDegrees = command.yaw;
      else this.startAbility(command.slot);
    }
  }

  private updateMovement(dt: number): void {
    const yaw = this.cameraYawDegrees * Math.PI / 180;
    const inputLength = Math.hypot(this.moveInput.forward, this.moveInput.right);
    const forward = inputLength > 0 ? this.moveInput.forward / Math.max(1, inputLength) : 0;
    const right = inputLength > 0 ? this.moveInput.right / Math.max(1, inputLength) : 0;
    const desired = {
      x: (-Math.sin(yaw) * forward + Math.cos(yaw) * right) * MAX_SPEED,
      z: (-Math.cos(yaw) * forward - Math.sin(yaw) * right) * MAX_SPEED,
    };
    const rate = inputLength > 0 ? ACCELERATION : DECELERATION;
    this.velocity.x = approach(this.velocity.x, desired.x, rate * dt);
    this.velocity.z = approach(this.velocity.z, desired.z, rate * dt);

    const abilityAge = this.activeAbility ? this.currentTime - this.activeAbility.startedAt : -1;
    const dash = this.activeAbility?.definition.dashDistance;
    if (dash && abilityAge >= this.activeAbility!.definition.phases.telegraph && abilityAge < this.activeAbility!.definition.phases.telegraph + this.activeAbility!.definition.phases.commit) {
      const dashSpeed = dash / this.activeAbility!.definition.phases.commit;
      this.velocity.x = Math.sin(this.player.facingYaw) * dashSpeed;
      this.velocity.z = Math.cos(this.player.facingYaw) * dashSpeed;
    }

    this.player.position.x = clamp(this.player.position.x + this.velocity.x * dt, -BOUNDS.x, BOUNDS.x);
    this.player.position.z = clamp(this.player.position.z + this.velocity.z * dt, -BOUNDS.z, BOUNDS.z);
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    if (speed > 0.08 && !this.activeAbility?.targetId) {
      const targetYaw = Math.atan2(this.velocity.x, this.velocity.z);
      this.player.facingYaw = approachAngle(this.player.facingYaw, targetYaw, dt * 10);
    }
    this.gaitProgress += speed * dt * 0.31;
  }

  private startAbility(slot: YardAbilitySlot): boolean {
    const definition = YARD_ABILITIES.find((candidate) => candidate.slot === slot);
    if (!definition || this.queuedAbility || (this.cooldownReadyAt.get(slot) ?? 0) > this.currentTime) return false;
    this.cooldownReadyAt.set(slot, this.currentTime + definition.cooldown);
    if (this.activeAbility) {
      const age = this.currentTime - this.activeAbility.startedAt;
      if (age >= chainStartAt(this.activeAbility.definition)) {
        this.beginAbility(definition, transitionSeedAt(this.activeAbility, age));
      } else {
        this.queuedAbility = definition;
      }
      return true;
    }
    this.beginAbility(definition);
    return true;
  }

  private beginAbility(definition: YardAbilityDefinition, transitionFrom?: ActionTransitionSeed): void {
    const target = this.nearestLivingDummy(definition.range, definition.arcDot);
    const eventId = this.nextId(`ability-${definition.slot}`);
    this.activeAbility = {
      definition,
      eventId,
      startedAt: this.currentTime,
      targetId: target?.id,
      impactApplied: false,
      transitionFrom,
    };
    this.addEvent({
      id: eventId,
      type: "act",
      entityId: PLAYER_ID,
      targetId: target?.id,
      action: definition.name,
      at: this.currentTime,
      phases: { ...definition.phases },
      tags: ["melee", definition.slot === 4 ? "area" : "physical"],
      motion: { ...definition.motion },
    });
  }

  private updateAbility(_dt: number): void {
    const active = this.activeAbility;
    if (!active) return;
    const age = this.currentTime - active.startedAt;
    const impactAt = active.definition.phases.telegraph + active.definition.phases.commit;
    if (!active.impactApplied && age >= impactAt) {
      active.impactApplied = true;
      for (const dummy of this.targetsFor(active.definition)) this.hitDummy(dummy, active);
    }
    if (this.queuedAbility && age >= chainStartAt(active.definition)) {
      const next = this.queuedAbility;
      this.queuedAbility = null;
      this.beginAbility(next, transitionSeedAt(active, age));
      return;
    }
    if (age >= abilityDuration(active.definition)) this.activeAbility = null;
  }

  private targetsFor(definition: YardAbilityDefinition): YardActor[] {
    return this.dummies.filter((dummy) => dummy.health > 0 && targetScore(this.player, dummy, definition.range, definition.arcDot) !== null);
  }

  private nearestLivingDummy(range: number, arcDot: number): YardActor | undefined {
    return this.dummies
      .map((dummy) => ({ dummy, score: dummy.health > 0 ? targetScore(this.player, dummy, range, arcDot) : null }))
      .filter((entry): entry is { dummy: YardActor; score: number } => entry.score !== null)
      .sort((a, b) => a.score - b.score)[0]?.dummy;
  }

  private hitDummy(dummy: YardActor, active: ActiveYardAbility): void {
    dummy.health = Math.max(0, dummy.health - active.definition.damage);
    if (dummy.health === 0) {
      dummy.reviveAt = this.currentTime + 2.8;
      this.addEvent({ id: this.nextId("status"), type: "status", entityId: dummy.id, status: "defeated", active: true, at: this.currentTime });
    }
    const direction = normalized({
      x: dummy.position.x - this.player.position.x,
      y: 0,
      z: dummy.position.z - this.player.position.z,
    });
    this.addEvent({
      id: this.nextId("impact"),
      type: "impact",
      sourceId: PLAYER_ID,
      targetId: dummy.id,
      outcome: "hit",
      amount: active.definition.damage,
      effect: active.definition.name,
      at: this.currentTime,
      motion: { direction, magnitude: Math.min(1, 0.56 + active.definition.damage * 0.2), pointHeight: 0.58 },
    });
  }

  private updateDummies(): void {
    for (const dummy of this.dummies) {
      if (dummy.reviveAt === null || this.currentTime < dummy.reviveAt) continue;
      dummy.health = dummy.maxHealth;
      dummy.reviveAt = null;
      this.addEvent({ id: this.nextId("status"), type: "status", entityId: dummy.id, status: "defeated", active: false, at: this.currentTime });
    }
  }

  private samplePlayer(): SampledEntity {
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    return {
      id: PLAYER_ID,
      label: "Wayfarer",
      kind: "actor",
      archetype: "duelist",
      presentationKit: COURT_WARRIOR_KIT_ID,
      team: "player",
      position: { ...this.player.position },
      rotation: { x: 0, y: this.player.facingYaw, z: 0 },
      scale: 0.72,
      appearance: {
        items: ["armor.warrior.breastplate", "armor.warrior.gauntlets", "weapon.arming-sword", "back.short-cape", "crest.plume"],
        colors: { body: "#3fa7d6", accent: "#b8f3ff", dark: "#17131b" },
      },
      anchors: { hud: { x: 0, y: 2.35, z: 0 } },
      vitals: { health: this.player.health, maxHealth: this.player.maxHealth },
      visible: true,
      movement: speed > 0.06 ? {
        gait: speed > MAX_SPEED * 0.72 ? "run" : "walk",
        progress: this.gaitProgress,
        intensity: Math.min(1, speed / MAX_SPEED),
        velocity: { x: this.velocity.x, y: 0, z: this.velocity.z },
      } : null,
      action: this.sampleAction(),
      statuses: [],
    };
  }

  private sampleDummy(dummy: YardActor): SampledEntity {
    return {
      id: dummy.id,
      label: dummy.label,
      kind: "actor",
      archetype: "dummy",
      team: "enemy",
      position: { ...dummy.position },
      rotation: { x: 0, y: dummy.facingYaw, z: 0 },
      scale: 0.58,
      appearance: { items: [], colors: { body: "#846d4c", accent: "#d6b978", dark: "#2b2118" } },
      anchors: { hud: { x: 0, y: 1.86, z: 0 } },
      vitals: { health: dummy.health, maxHealth: dummy.maxHealth },
      visible: true,
      movement: null,
      action: null,
      statuses: dummy.health <= 0 ? ["defeated"] : [],
    };
  }

  private sampleAction(): ActiveAction | null {
    const active = this.activeAbility;
    if (!active) return null;
    const elapsed = this.currentTime - active.startedAt;
    const { phase, phaseProgress } = abilityPhaseAt(active.definition, elapsed);
    return {
      eventId: active.eventId,
      action: active.definition.name,
      targetId: active.targetId,
      phase,
      phaseProgress,
      totalProgress: clamp(elapsed / abilityDuration(active.definition), 0, 1),
      tags: ["melee"],
      motion: { ...active.definition.motion },
      transitionFrom: active.transitionFrom ? {
        motion: { ...active.transitionFrom.motion },
        phase: active.transitionFrom.phase,
        phaseProgress: active.transitionFrom.phaseProgress,
      } : undefined,
    };
  }

  private sampleImpacts(): ActiveImpact[] {
    return this.history
      .filter((event): event is Extract<PresentationEvent, { type: "impact" }> => event.type === "impact")
      .map((event) => ({ event, age: this.currentTime - event.at }))
      .filter(({ age }) => age >= 0 && age <= 0.42)
      .map(({ event, age }) => ({
        eventId: event.id,
        sourceId: event.sourceId,
        targetId: event.targetId,
        outcome: event.outcome,
        amount: event.amount,
        effect: event.effect,
        motion: event.motion,
        age,
      }));
  }

  private addEvent(event: PresentationEvent): void {
    this.history.push(event);
    if (this.history.length > HISTORY_LIMIT) this.history.splice(0, this.history.length - HISTORY_LIMIT);
  }

  private nextId(kind: string): string {
    this.eventSequence += 1;
    return `yard-${kind}-${this.eventSequence}`;
  }
}

function actor(id: string, label: string, x: number, z: number, health: number): YardActor {
  return { id, label, position: { x, y: 0, z }, facingYaw: Math.PI, health, maxHealth: health, reviveAt: null };
}

function yardMarkers(): SampledEntity[] {
  return [
    { id: "yard-ring-left", label: "Practice ring", kind: "zone", archetype: "yard-marker", team: "neutral", position: { x: -3.8, y: 0, z: -2.7 }, scale: 0.85, visible: true, movement: null, action: null, statuses: [] },
    { id: "yard-ring-right", label: "Practice ring", kind: "zone", archetype: "yard-marker", team: "neutral", position: { x: 3.8, y: 0, z: -2.7 }, scale: 0.85, visible: true, movement: null, action: null, statuses: [] },
  ];
}

function targetScore(source: YardActor, target: YardActor, range: number, arcDot: number): number | null {
  const dx = target.position.x - source.position.x;
  const dz = target.position.z - source.position.z;
  const distance = Math.hypot(dx, dz);
  if (distance > range || distance < 1e-5) return null;
  const forwardX = Math.sin(source.facingYaw);
  const forwardZ = Math.cos(source.facingYaw);
  const dot = (dx / distance) * forwardX + (dz / distance) * forwardZ;
  return dot >= arcDot ? distance : null;
}

function abilityDuration(definition: YardAbilityDefinition): number {
  return definition.phases.telegraph + definition.phases.commit + definition.phases.impact + definition.phases.recover;
}

function chainStartAt(definition: YardAbilityDefinition): number {
  return definition.phases.telegraph + definition.phases.commit + definition.phases.impact;
}

function abilityPhaseAt(
  definition: YardAbilityDefinition,
  elapsed: number,
): { phase: ActionPhase; phaseProgress: number } {
  let phase: ActionPhase = "recover";
  let phaseStart = 0;
  for (const candidate of ["telegraph", "commit", "impact", "recover"] as const) {
    const duration = definition.phases[candidate];
    if (elapsed < phaseStart + duration) {
      phase = candidate;
      break;
    }
    phaseStart += duration;
  }
  return {
    phase,
    phaseProgress: clamp((elapsed - phaseStart) / definition.phases[phase], 0, 1),
  };
}

function transitionSeedAt(active: ActiveYardAbility, elapsed: number): ActionTransitionSeed {
  const sampled = abilityPhaseAt(active.definition, elapsed);
  return createActionTransitionSeed(
    active.definition.motion,
    sampled.phase,
    sampled.phaseProgress,
    active.transitionFrom,
  );
}

function normalized(vector: Vec3): Vec3 {
  const length = Math.hypot(vector.x, vector.y, vector.z);
  return length < 1e-5 ? { x: 0, y: 0, z: 1 } : { x: vector.x / length, y: vector.y / length, z: vector.z / length };
}

function approach(value: number, target: number, delta: number): number {
  if (value < target) return Math.min(target, value + delta);
  return Math.max(target, value - delta);
}

function approachAngle(value: number, target: number, delta: number): number {
  const difference = Math.atan2(Math.sin(target - value), Math.cos(target - value));
  return value + clamp(difference, -delta, delta);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
